// campaigns-redesign-view.tsx — route shell for the Drip Campaigns redesign.
//
// EVERYTHING is route-driven (see docs/design-handoffs/drip-redesign-routing.md).
// The shell derives which surface + which wizard step to render purely from the
// current location — there is no local `route`/`launched`/`surface`-prop state.
//
//   /campaigns                         → list (CampaignsList; ?tab in the query)
//   /campaigns/new                     → creates a draft Drip, redirects to ↓
//   /campaigns/from-template           → pick a saved drip template → seeds a draft Drip → ↓
//   /campaigns/:id/edit/:step          → wizard (step ∈ audience|message|timing|review)
//   /campaigns/:id/launched            → post-launch confirmation
//   /campaigns/:id                     → sequence detail (read)
//   /campaigns/audiences/:boardId/edit → audience-board contacts editor (sub-route)
//   /campaigns/templates/:templateId/edit → template editor (sub-route)
//
// Wraps everything in `.campaigns-rd` (design tokens from
// styles/campaigns-redesign.css).

import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

import '@fontsource/geist/400.css';
import { BANNER_OFFSET_HEIGHT, BANNER_OFFSET_TOP } from '@common/components/dunning-banner/banner-offset';
import '@fontsource/geist/500.css';
import '@fontsource/geist/600.css';
import '@fontsource/geist/700.css';
import '@fontsource/geist-mono/400.css';
import '@fontsource/geist-mono/500.css';
import '@fontsource/geist-mono/600.css';
import '@/styles/campaigns-redesign.css';

import { CampaignsList } from '@extension/context/content/components/campaigns-redesign/management-list';
import { WIZARD_STEP_IDS, type WizardStepId } from '@extension/context/content/components/campaigns-redesign/wizard';
import { CampaignEditRoute } from '@extension/context/content/components/campaigns-redesign/campaign-edit-route';
import { CampaignLaunched } from '@extension/context/content/components/campaigns-redesign/launched';
import { SequenceDetail } from '@extension/context/content/components/campaigns-redesign/sequence-detail';
import { parseCampaignDetailTab } from '@extension/context/content/components/campaigns-redesign/campaign-setup-edit';
import { CampaignSubEditor, CampaignLogsRoute } from '@extension/context/content/components/campaigns-redesign/campaign-state-bridge';
import { CampaignDraftCreator } from '@extension/context/content/components/campaigns-redesign/campaign-draft-creator';
import { DripTemplatePicker } from '@extension/context/content/components/campaigns-redesign/drip-template-picker';
import {
  CampaignsRedesignNavProvider,
  recordCampaignsPath,
  type CampaignsRedesignNav,
} from '@extension/context/content/components/campaigns-redesign/nav-context';
import { useTranslation } from 'react-i18next';

import AppView from '@common/components/app-view';
import { isExtensionContext } from '@common/services/detect-context';
import { isRtlLanguage } from '@common/services/rtl';
import { namespace } from '@common/config/constants';
import { useRouterBack } from '@common/hooks/use-router-back';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { closeBulkDashboard } from '@common-reducers/CampaignReducer';
import { mainSliceActions } from '@common-reducers/MainReducer';
import { selectIsSystemDarkMode } from '@common-reducers/UserSelectors';
import { getDarkModeFromBodyClass } from '@common/components/bt-theme-provider';
import { flowPath } from '@common/utils/clear-flow';

type RedesignTheme = 'light' | 'dark';
type RedesignDensity = 'compact' | 'regular' | 'comfy';

interface CampaignsRedesignViewProps {
  theme?: RedesignTheme;
  density?: RedesignDensity;
  /** Chromeless mode for the `/clear-campaigns/*` routes — see AppView's `bare`. */
  bare?: boolean;
}

// Which surface the current path resolves to. Derived from the location only —
// no prop, no guessing, no local state.
type Surface =
  | { kind: 'list' }
  | { kind: 'new' }
  | { kind: 'wizard'; id: string; step: WizardStepId }
  | { kind: 'launched'; id: string }
  | { kind: 'detail'; id: string }
  | { kind: 'audience-edit'; boardId: string }
  | { kind: 'template-edit'; templateId: string }
  | { kind: 'logs'; id: string }
  | { kind: 'template-new' }
  | { kind: 'drip-template-apply' };

const isWizardStep = (s: string | undefined): s is WizardStepId =>
  !!s && (WIZARD_STEP_IDS as readonly string[]).includes(s);

/**
 * Tracks the document-level `.dark` class (WhatsApp sets it on its own <body>
 * in the extension; `setDarkModeClasses` sets it in the web app), so this shell
 * resolves the same dark signal the global token file keys on.
 */
function useDocumentDarkClass(): boolean {
  const [isDark, setIsDark] = React.useState<boolean>(() => {
    try { return getDarkModeFromBodyClass(); } catch { return false; }
  });
  React.useEffect(() => {
    const read = () => setIsDark(getDarkModeFromBodyClass());
    read();
    const observer = new MutationObserver(read);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    if (document.body) observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
  return isDark;
}

const CampaignsRedesignView: React.FC<CampaignsRedesignViewProps> = ({
  theme,
  density = 'regular',
  bare = false,
}) => {
  // Follow WhatsApp/system dark mode so the redesign shell and the embedded
  // legacy panels (Tailwind `dark:`, keyed on the ancestor `.dark` that WhatsApp
  // dark mode sets) stay in sync — light on light, dark on dark. An explicit
  // `theme` prop (the website passes one) still wins.
  const isSystemDark = useAppSelector(selectIsSystemDarkMode);
  // `data-theme` below and the document-level `.dark` class are the app's two
  // dark switches, and the global token file now flips on BOTH (see the "THE
  // DARK SWITCH" block in color-scheme-tokens.css). For that to hold they must
  // never disagree, so this reads the SAME signal the class does rather than a
  // second one.
  //
  // `selectIsSystemDarkMode` is `whatsappDarkMode`, which is only ever written
  // by the extension's MutationObserver on WhatsApp's <body class="dark">. In
  // the WEB APP nothing writes it, so a user whose own dark mode is on got
  // `body.dark` (global tokens dark) next to `data-theme="light"` (this
  // shell's tokens light) — the preview bubble's dark ink on its dark ground
  // measured 2.02:1. Reading the class as well makes the two agree in both
  // hosts; an explicit `theme` prop (the website passes one) still wins.
  const isDocumentDark = useDocumentDarkClass();
  const resolvedTheme: RedesignTheme = theme ?? ((isSystemDark || isDocumentDark) ? 'dark' : 'light');
  // The redesign renders inside a shadow-DOM overlay in the extension, so it does
  // NOT inherit the document `dir` the app sets for RTL languages — it has to
  // declare its own, or every campaign surface lays out LTR for Hebrew/Arabic.
  const { t, i18n } = useTranslation(namespace);
  const isRtl = isRtlLanguage(i18n.language);
  const { id, step, boardId, templateId } = useParams<{
    id: string;
    step: string;
    boardId: string;
    templateId: string;
  }>();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Record the live destination so the outgoing wizard's unmount cleanup can
  // tell a sub-editor detour (audiences/templates) from a real exit and NOT
  // reclaim a draft the user is about to return to. The shell re-renders with
  // the new location before that cleanup runs, so this is the DESTINATION path.
  recordCampaignsPath(pathname);

  // Back = pop the history stack (return to wherever we came from — e.g. the
  // detail after "View delivery logs", not always the list); falls back to the
  // list on a cold/deep-link entry. Router-agnostic (works under the extension's
  // MemoryRouter too) — see useRouterBack.
  const goBack = useRouterBack(flowPath('/campaigns'));
  // In the extension the redesign is a shadow-DOM overlay on web.whatsapp.com —
  // the surrounding app shell (BoardsPanel left nav, header) is redundant there,
  // so we render the component chromeless + a floating close that dismisses the
  // overlay (closeBulkDashboard → CampaignReducer.open=false). The website keeps
  // the full AppView shell.
  const embedded = isExtensionContext();

  // Back returns to where the campaign was opened FROM. From the planner (the
  // calendar marks its links `?from=calendar`, see campaignPagePath) that is
  // the planner: on the website the history pop (or /calendar on a cold
  // link); in the extension the planner is its own overlay, outside this
  // memory router, so Back closes this overlay and reopens it — popping the
  // memory history instead landed on the campaigns list. Everything else keeps
  // the history-based goBack.
  const openedFromCalendar = React.useMemo(() => new URLSearchParams(search).get('from') === 'calendar', [search]);
  const goBackToCalendarRoute = useRouterBack('/calendar');
  const backFromDetail = React.useCallback(() => {
    if (!openedFromCalendar) return goBack();
    if (embedded) {
      dispatch(closeBulkDashboard());
      dispatch(mainSliceActions.toggleCalendarView(true));
    } else {
      goBackToCalendarRoute();
    }
  }, [openedFromCalendar, embedded, goBack, goBackToCalendarRoute, dispatch]);

  // URL-driven navigation contract shared with the reused legacy leaf
  // components (audience rows, template cards, editor back-buttons) via context.
  // Every callback navigates to a route the shell can resolve on cold reload.
  // `closeBoard`/`closeTemplate` land on the right tab list.
  const nav = React.useMemo<CampaignsRedesignNav>(
    () => ({
      openBoard: (id) => navigate(flowPath(`/campaigns/audiences/${id}/edit`)),
      openTemplate: (id) => navigate(flowPath(`/campaigns/templates/${id}/edit`)),
      openLogs: (id) => navigate(flowPath(`/campaigns/${id}/logs`)),
      editCampaign: (id, step = 'audience') => navigate(flowPath(`/campaigns/${id}/edit/${step}`)),
      newTemplate: () => navigate(flowPath('/campaigns/templates/new')),
      newAudience: () => navigate(flowPath('/campaigns?tab=audiences&new=audience')),
      // Pop the stack so a sub-editor entered FROM the wizard returns to the
      // wizard (not the tab list). The origin tab is preserved via history; the
      // fallback only applies on a cold deep-link with nothing to pop.
      closeBoard: goBack,
      closeTemplate: goBack,
    }),
    [navigate, goBack],
  );

  // A calendar chip is one STEP of a sequence, and the click carries that step's
  // index in the query (`?step=2`). Read here rather than in the detail so the
  // single place that owns route→surface translation owns this too; `useLocation`
  // resolves under BOTH routers, which a `window.location` read would not.
  const focusStepIndex = React.useMemo(() => {
    const raw = new URLSearchParams(search).get('step');
    if (raw === null) return undefined;
    const n = Number(raw);
    return Number.isInteger(n) && n >= 0 ? n : undefined;
  }, [search]);
  // `?tab=audience|when` opens the campaign page on that tab.
  const initialDetailTab = React.useMemo(
    () => parseCampaignDetailTab(new URLSearchParams(search).get('tab')),
    [search],
  );

  const surface = React.useMemo<Surface>(() => {
    if (boardId) return { kind: 'audience-edit', boardId };
    if (templateId) return { kind: 'template-edit', templateId };
    // Template create sub-route — matched on the path (no entity id param). Must
    // precede the bare `/new` (drip create) check below.
    if (pathname.endsWith('/templates/new')) return { kind: 'template-new' };
    // FE#331 — apply a saved drip template (must precede the bare `/new` check;
    // it does not end with `/new`, but keep the ordering explicit).
    if (pathname.endsWith('/from-template')) return { kind: 'drip-template-apply' };
    if (pathname.endsWith('/new')) return { kind: 'new' };
    if (id && pathname.endsWith('/launched')) return { kind: 'launched', id };
    if (id && pathname.endsWith('/logs')) return { kind: 'logs', id };
    if (id && step) {
      // Unknown step → fall back to the first wizard step (defensive deep-link).
      return { kind: 'wizard', id, step: isWizardStep(step) ? step : 'audience' };
    }
    if (id) return { kind: 'detail', id };
    return { kind: 'list' };
  }, [id, step, boardId, templateId, pathname]);

  const content = (
      <div
        className="campaigns-rd"
        dir={isRtl ? 'rtl' : 'ltr'}
        data-theme={resolvedTheme}
        data-density={density}
        style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg-canvas)' }}
      >
        <CampaignsRedesignNavProvider value={nav}>
        {surface.kind === 'list' && <CampaignsList />}

        {surface.kind === 'new' && <CampaignDraftCreator />}

        {surface.kind === 'drip-template-apply' && <DripTemplatePicker onBack={goBack} />}

        {surface.kind === 'wizard' && (
          <CampaignEditRoute key={surface.id} id={surface.id} step={surface.step} onBack={goBack} />
        )}

        {surface.kind === 'launched' && (
          <CampaignLaunchedRoute id={surface.id} />
        )}

        {surface.kind === 'detail' && (
          <SequenceDetail dripId={surface.id} onBack={backFromDetail} focusStepIndex={focusStepIndex} initialTab={initialDetailTab} />
        )}

        {surface.kind === 'audience-edit' && (
          <CampaignSubEditor kind="audience" entityId={surface.boardId} onBack={nav.closeBoard} />
        )}

        {surface.kind === 'template-edit' && (
          <CampaignSubEditor kind="template" entityId={surface.templateId} onBack={nav.closeTemplate} />
        )}

        {surface.kind === 'template-new' && (
          <CampaignSubEditor kind="template-new" onBack={nav.closeTemplate} />
        )}

        {surface.kind === 'logs' && (
          <CampaignLogsRoute campaignId={surface.id} onBack={goBack} />
        )}
        </CampaignsRedesignNavProvider>
      </div>
  );

  if (embedded) {
    // The overlay host (#campaigns-view) collapses to 0 height (its shadow
    // content is absolutely positioned), so fill the viewport explicitly with
    // 100vh — this is what AppView's .pageContainer did before.
    return (
      <div dir={isRtl ? 'rtl' : 'ltr'} style={{ position: 'relative', height: BANNER_OFFSET_HEIGHT, marginTop: BANNER_OFFSET_TOP, width: '100%', overflow: 'hidden' }}>
        <button
          type="button"
          aria-label={t('common.close', { defaultValue: 'Close' })}
          title={t('common.close', { defaultValue: 'Close' })}
          onClick={() => dispatch(closeBulkDashboard())}
          style={{
            position: 'absolute', top: 12, insetInlineEnd: 12, zIndex: 50,
            width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: 8, border: '1px solid var(--border-subtle, rgba(0,0,0,0.08))',
            background: 'var(--bg-surface, #fff)', color: 'var(--text-secondary, #555)',
            cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
        {content}
      </div>
    );
  }

  return (
    <AppView activeExtensionView="campaigns" showHeader={false} bare={bare}>
      {content}
    </AppView>
  );
};

// Post-launch confirmation. `multi` is derived from the launched drip itself
// (loaded by SequenceDetail's selector), so a reload of /campaigns/:id/launched
// still renders the right copy.
const CampaignLaunchedRoute: React.FC<{ id: string }> = ({ id }) => {
  const navigate = useNavigate();
  return (
    <CampaignLaunched
      dripId={id}
      // replace: the launched screen is terminal — Track progress / New campaign
      // must not leave it (or the wizard) on the back-stack, so Back from the
      // detail returns to the list rather than walking back through the flow.
      onView={() => navigate(flowPath(`/campaigns/${id}`), { replace: true })}
      onAnother={() => navigate(flowPath('/campaigns/new'), { replace: true })}
    />
  );
};

export default CampaignsRedesignView;
